// @flow

import dompurify from 'dompurify';
import { rem } from 'polished';
import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-awesome-styled-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import styled, { type StyledComponent } from 'styled-components';

import loaderTokenImage from '../../../assets/images/loader-token.gif';
import { __ } from '../../../i18n/l10n';
import { useSubSteps, LeftSide, GroupPush } from '../../../modules/shared';
import {
  WS_CHECK_DEPOSIT_REQUEST,
  GLOBAL_LOADING,
  WS_DISPLAY_ERROR_LAYER,
} from '../../../store/actions';
import type { RootState } from '../../../store/types';
import { fontFamily, sizes } from '../../../styles/GlobalStyle';
import { API } from '../../../utils/APIUtils';

type CheckDepositRawProps = {
  className?: string,
};

const CheckDepositRaw = ({ className }: CheckDepositRawProps) => {
  const sanitizer = dompurify.sanitize;
  const { depositId } = useParams();
  const dispatch = useDispatch();
  const websocket = useSelector((state: RootState) => state.websocket);
  const deposit = useSelector((state: RootState) => state.deposit);
  const userRegistration = useSelector((state: RootState) => state.userRegistration);
  const history = useHistory();
  const [nbTries, setNbTries] = useState(0);
  const [currentSubStep, changeSubStep] = useSubSteps();

  useEffect(() => {
    if (depositId === 'bank-transfer') {
      changeSubStep(1);
      return;
    }

    dispatch({
      type: GLOBAL_LOADING,
      payload: {
        spine: 'token',
        fallback: loaderTokenImage,
        title: __('Verification'),
        text: __('of the ongoing payment...'),
      },
    });

    if (websocket.ready) {
      dispatch({
        type: WS_CHECK_DEPOSIT_REQUEST,
        payload: {
          method: API.CheckDeposit,
          params: {
            deposit_id: depositId,
          },
        },
      });
    }
  }, [changeSubStep, depositId, dispatch, websocket.ready]);

  useEffect(() => {
    if (deposit.checkDeposit.response) {
      const isFirstDeposit = !userRegistration.user || !userRegistration.user.first_deposit;

      if (deposit.checkDeposit.response.status === 'success') {
        if (isFirstDeposit) {
          history.replace('/step7');
        } else {
          changeSubStep(1);
        }
        dispatch({
          type: GLOBAL_LOADING,
          payload: false,
        });
      } else if (deposit.checkDeposit.response.status === 'processing') {
        const newNbTries = nbTries + 1;
        setNbTries(newNbTries);
        if (newNbTries < 3) {
          setTimeout(() => {
            dispatch({
              type: WS_CHECK_DEPOSIT_REQUEST,
              payload: {
                method: API.CheckDeposit,
                params: {
                  deposit_id: depositId,
                },
              },
            });
          }, 10000);
        } else {
          if (isFirstDeposit) {
            history.replace('/step7');
          }
          dispatch({
            type: GLOBAL_LOADING,
            payload: false,
          });
          dispatch({
            type: WS_DISPLAY_ERROR_LAYER,
            payload: {
              title: __('Your payment'),
              message: __('is being processed...'),
            },
          });
        }
      } else if (deposit.checkDeposit.response.status === 'failed') {
        dispatch({
          type: WS_DISPLAY_ERROR_LAYER,
          payload: {
            message: deposit.checkDeposit.response.error.message,
          },
        });
      }
    }
  }, [
    changeSubStep,
    deposit.checkDeposit.response,
    depositId,
    dispatch,
    history,
    nbTries,
    userRegistration.user,
  ]);

  // __('Your account is reloaded,<br /><b>you can continue playing on Winamax</b>')
  return (
    <div className={className}>
      {currentSubStep === 1 && (
        <Container className={className}>
          <Row className={'row'}>
            <LeftSide
              title={''}
              titleMobile={__('Deposit')}
            />
            <Col
              className={'column right-side'}
              xs={12}
              md={5}
            >
              <div className={'step'}>
                <div
                  className={'success-title'}
                  dangerouslySetInnerHTML={{
                    __html: sanitizer(
                      __(
                        'Your account is reloaded,<br /><b>you can continue playing on Winamax</b>',
                      ),
                    ),
                  }}
                />
                <GroupPush />
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};

const CheckDeposit: StyledComponent<CheckDepositRawProps, {}, typeof CheckDepositRaw> = styled(
  CheckDepositRaw,
)`
  .success-title {
    text-transform: uppercase;
    font-size: ${rem(28)};
    line-height: 36px;
    font-family: ${fontFamily.primary.black.fontFamily};
    font-weight: ${fontFamily.primary.black.fontWeight};
    margin-bottom: ${sizes.paddingBig};
  }
`;

export default CheckDeposit;
